import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Knopf } from './Knopf';

export interface DialogProps {
  offen: boolean;
  titel: string;
  beschreibung?: ReactNode;
  children?: ReactNode;
  aufBestaetigen?: () => void;
  aufAbbrechen: () => void;
  bestaetigenText?: string;
  abbrechenText?: string;
  gefahr?: boolean;
  laedt?: boolean;
  breit?: boolean;
}

/**
 * Ein einzelner Dialog, kein Modal-Stapel (SPEC Kap. 12).
 * Nutzt `<dialog>`, damit Fokusfalle und Escape vom Browser kommen.
 */
export const Dialog = ({
  offen,
  titel,
  beschreibung,
  children,
  aufBestaetigen,
  aufAbbrechen,
  bestaetigenText = 'Bestätigen',
  abbrechenText = 'Abbrechen',
  gefahr = false,
  laedt = false,
  breit = false,
}: DialogProps) => {
  const referenz = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = referenz.current;
    if (!element) return;
    if (offen && !element.open) element.showModal();
    if (!offen && element.open) element.close();
  }, [offen]);

  useEffect(() => {
    const element = referenz.current;
    if (!element) return;
    const beiAbbruch = (ereignis: Event): void => {
      ereignis.preventDefault();
      if (!laedt) aufAbbrechen();
    };
    element.addEventListener('cancel', beiAbbruch);
    return () => element.removeEventListener('cancel', beiAbbruch);
  }, [aufAbbrechen, laedt]);

  return (
    <dialog
      ref={referenz}
      aria-labelledby="dialog-titel"
      className={cn(
        'm-auto w-[min(94vw,32rem)] rounded-xl bg-white p-0 shadow-xl backdrop:bg-slate-900/50',
        breit && 'w-[min(94vw,56rem)]',
        'dunkel:bg-slate-900 dunkel:text-slate-100',
      )}
    >
      <div className="p-5">
        <h2 id="dialog-titel" className="text-lg font-semibold">
          {titel}
        </h2>
        {beschreibung && (
          <div className="mt-2 text-sm text-slate-600 dunkel:text-slate-300">{beschreibung}</div>
        )}
        {children && <div className="mt-4">{children}</div>}
      </div>
      <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end dunkel:border-slate-700">
        <Knopf aussehen="sekundaer" onClick={aufAbbrechen} disabled={laedt}>
          {abbrechenText}
        </Knopf>
        {aufBestaetigen && (
          <Knopf aussehen={gefahr ? 'gefahr' : 'primaer'} onClick={aufBestaetigen} laedt={laedt}>
            {bestaetigenText}
          </Knopf>
        )}
      </div>
    </dialog>
  );
};
